import { DialogContent, Stack, type Theme } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { BottomSheet, Text, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import parachuteGiftImage from "@src/appV2/redesign/system/ui-components/illustrations/assets/parachute-gift.png";

import { type ResolvedCancellationProtectionPayout } from "../api/types";
import { getTotalPayoutAmountInMinorUnits } from "../api/utils";
import { PayoutShiftCard } from "./PayoutShiftCard";

interface CancellationProtectionPayoutBottomSheetProps {
  modalState: UseModalState;
  payout: ResolvedCancellationProtectionPayout;
  isUpdating: boolean;
  onClose: () => void;
}

export function CancellationProtectionPayoutBottomSheet(
  props: CancellationProtectionPayoutBottomSheetProps
) {
  const { modalState, payout, isUpdating, onClose } = props;

  useLogEffect(
    APP_V2_USER_EVENTS.CANCELLATION_PROTECTION_PAYOUT_VIEWED,
    { payoutId: payout.id },
    { enabled: modalState.modalIsOpen }
  );

  return (
    <BottomSheet
      disableSwipeArea
      modalState={modalState}
      canDismiss={false}
      paperSx={(theme: Theme) => ({
        backgroundColor: theme.palette.background.quaternary,
      })}
      header={<BottomSheetHeader closeButtonDisabled={isUpdating} onClose={onClose} />}
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isUpdating}
            onClick={onClose}
          >
            Great!
          </LoadingButton>
        </DialogFooter>
      }
    >
      <DialogContent
        sx={{
          paddingX: 5,
          paddingTop: 9,
          paddingBottom: 11,
        }}
      >
        <Stack spacing={8} alignItems="center">
          <img src={parachuteGiftImage} alt="" width={105} height={120} />
          <Stack spacing={3} alignItems="center">
            <Text
              semibold
              variant="h4"
              sx={(theme: Theme) => ({
                color: theme.palette.text.primary,
                textAlign: "center",
              })}
            >
              {`You'll receive ${formatCentsAsUsd(getTotalPayoutAmountInMinorUnits(payout))} due to Cancellation Protection`}
            </Text>
            <Text
              variant="body2"
              sx={(theme: Theme) => ({
                color: theme.palette.text.primary,
                textAlign: "center",
              })}
            >
              Your earnings will be sent directly to your payout destination
            </Text>
          </Stack>
          <PayoutShiftCard payout={payout} />
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
